import {
	addMoney,
	addPercentageToMoney,
	createMoney,
	divideMoneyBy,
	formatMoneyByDecimal,
	isZeroMoney,
	moneyParseByDecimal,
	multipleMoneyBy,
	subtractMoney,
	TMoney,
} from "@sembark-travel/money"
import {
	IAvailability,
	IAvailabilityRateOption,
	IAvailabilityRoomWiseRateOptions,
	IChildSlotOption,
	IHotelRemoteRoomType,
	THotelRatePricingType,
} from "./store"
import { IConfirmedRoomSelection, TNightlyPrice } from "./Item"
import { THotelPriceBreakdowns } from "../utils"
import { TChildrenArray } from "../Tourists"
import { addUnit, formatDate } from "@sembark-travel/datetime-utils"
import pluralize from "pluralize"

function findRate(
	rateOption: IAvailabilityRateOption,
	date: string,
	occupancy: string,
	discountFactor = 1
): TMoney | undefined {
	const dateEntry = rateOption.dates.find((d) => d.date_local === date)
	const rate = dateEntry?.rates.find(
		(r) => r.occupancy === occupancy
	)?.rate_before_tax_money
	return rate ? multipleMoneyBy(rate, discountFactor) : undefined
}

export function getRoomBasePrice(
	rateOption: IAvailabilityRateOption,
	ppr: number,
	date: string,
	discountFactor = 1
): { tier: string; amount: TMoney } | undefined {
	const tierRate = findRate(rateOption, date, `A${ppr}`, discountFactor)
	if (tierRate !== undefined) return { tier: `A${ppr}`, amount: tierRate }

	const baseRate = findRate(rateOption, date, "BASE", discountFactor)

	if (baseRate !== undefined) return { tier: "BASE", amount: baseRate }

	return undefined
}

export type TRoomNightAllocation = {
	room_index: number
	breakdown: TRoomNightPriceBreakdown
	original_breakdown: TRoomNightPriceBreakdown
	total: TMoney
	original_total: TMoney
}

/**
 * GST slab: per-room total (before tax) ≤ 7500 INR → 5%, > 7500 → 18%.
 */
function getGSTRate(perRoomTotalBeforeTax: TMoney): number {
	const decimal = Number(formatMoneyByDecimal(perRoomTotalBeforeTax))
	return decimal <= 7500 ? 5 : 18
}

type TRoomExtraDistribution = {
	aweb: number
	cwebAges: Array<number>
	cnbAges: Array<number>
}

export type TTaxStrategyResult = {
	strategy: "fill_base" | "round_robin"
	distribution: Array<TRoomExtraDistribution>
	totalAfterTax: TMoney
	roomTaxRates: Array<number>
}

// Oldest first, every time — the same set of children must distribute the
// same way regardless of the order they were selected/added in the UI.
function sortAgesDescending(ages: Array<number>): Array<number> {
	return [...ages].sort((a, b) => b - a)
}

/**
 * Phase A — absorb AWEB into empty base-occupancy slots (baseOccupancy - ppr
 * per room). Absorbed AWEBs still get charged (chargeDist), but are excluded
 * from slabDist so they don't push the room's GST slab up.
 * Phase B — shared-cursor queue for whatever's left (remaining AWEB, then all
 * CWEB, then all CNB), respecting per-room caps — same shared-cursor trick as
 * our existing distribution, so different pax types don't all concentrate on
 * room 0.
 */
function distributeExtrasFillBase(
	noOfRooms: number,
	personsPerRoom: number,
	awebCount: number,
	cwebAges: Array<number>,
	cnbAges: Array<number>,
	constraints: {
		baseOccupancy: number
		allowedExtraBeds: number
		allowedAwebPerRoom: number
		allowedCwebPerRoom: number
		allowedCnbPerRoom: number | null
		maxAdultOccupancy: number
		maxChildOccupancy: number
	}
): {
	slabDist: Array<TRoomExtraDistribution>
	chargeDist: Array<TRoomExtraDistribution>
} {
	const {
		baseOccupancy,
		allowedExtraBeds,
		allowedAwebPerRoom,
		allowedCwebPerRoom,
		allowedCnbPerRoom,
		maxAdultOccupancy,
		maxChildOccupancy,
	} = constraints

	const slabDist: Array<TRoomExtraDistribution> = Array.from(
		{ length: noOfRooms },
		() => ({ aweb: 0, cwebAges: [], cnbAges: [] })
	)
	const chargeDist: Array<TRoomExtraDistribution> = Array.from(
		{ length: noOfRooms },
		() => ({ aweb: 0, cwebAges: [], cnbAges: [] })
	)

	const emptySlots = Math.max(0, baseOccupancy - personsPerRoom)
	let remainingAweb = awebCount
	for (let i = 0; i < noOfRooms && remainingAweb > 0; i++) {
		const absorb = Math.min(emptySlots, remainingAweb)
		chargeDist[i].aweb += absorb
		remainingAweb -= absorb
	}

	type QueueItem =
		| { kind: "aweb" }
		| { kind: "cweb"; age: number }
		| { kind: "cnb"; age: number }

	const queue: Array<QueueItem> = [
		...Array.from(
			{ length: remainingAweb },
			(): QueueItem => ({ kind: "aweb" })
		),
		...sortAgesDescending(cwebAges).map(
			(age): QueueItem => ({ kind: "cweb", age })
		),
		...sortAgesDescending(cnbAges).map(
			(age): QueueItem => ({ kind: "cnb", age })
		),
	]

	let cursor = 0
	for (const item of queue) {
		let assigned = false
		for (let attempt = 0; attempt < noOfRooms; attempt++) {
			const roomIdx = (cursor + attempt) % noOfRooms
			const d = slabDist[roomIdx]
			const total = d.aweb + d.cwebAges.length + d.cnbAges.length

			if (total >= allowedExtraBeds) continue
			if (item.kind === "aweb" && d.aweb >= allowedAwebPerRoom) continue
			if (item.kind === "aweb" && d.aweb >= maxAdultOccupancy - personsPerRoom)
				continue
			if (item.kind === "cweb" && d.cwebAges.length >= allowedCwebPerRoom)
				continue
			if (
				item.kind === "cnb" &&
				allowedCnbPerRoom !== null &&
				d.cnbAges.length >= allowedCnbPerRoom
			)
				continue
			if (
				(item.kind === "cweb" || item.kind === "cnb") &&
				d.cwebAges.length + d.cnbAges.length >= maxChildOccupancy
			)
				continue

			// Same room in both slabDist and chargeDist — no mismatch possible.
			if (item.kind === "aweb") {
				slabDist[roomIdx].aweb++
				chargeDist[roomIdx].aweb++
			} else if (item.kind === "cweb") {
				slabDist[roomIdx].cwebAges.push(item.age)
				chargeDist[roomIdx].cwebAges.push(item.age)
			} else {
				slabDist[roomIdx].cnbAges.push(item.age)
				chargeDist[roomIdx].cnbAges.push(item.age)
			}
			cursor = (roomIdx + 1) % noOfRooms
			assigned = true
			break
		}

		if (!assigned) {
			const roomIdx = cursor % noOfRooms
			if (item.kind === "aweb") {
				slabDist[roomIdx].aweb++
				chargeDist[roomIdx].aweb++
			} else if (item.kind === "cweb") {
				slabDist[roomIdx].cwebAges.push(item.age)
				chargeDist[roomIdx].cwebAges.push(item.age)
			} else {
				slabDist[roomIdx].cnbAges.push(item.age)
				chargeDist[roomIdx].cnbAges.push(item.age)
			}
			cursor = (cursor + 1) % noOfRooms
		}
	}

	return { slabDist, chargeDist }
}

function distributeExtrasRoundRobin(
	noOfRooms: number,
	personsPerRoom: number,
	awebCount: number,
	cwebAges: Array<number>,
	cnbAges: Array<number>,
	constraints: {
		allowedExtraBeds: number
		allowedAwebPerRoom: number
		allowedCwebPerRoom: number
		allowedCnbPerRoom: number | null
		maxAdultOccupancy: number
		maxChildOccupancy: number
	}
): Array<TRoomExtraDistribution> {
	const rooms: Array<TRoomExtraDistribution> = Array.from(
		{ length: noOfRooms },
		() => ({ aweb: 0, cwebAges: [], cnbAges: [] })
	)

	function assign(
		kind: "aweb" | "cweb" | "cnb",
		age: number | undefined,
		startCursor: number
	): number {
		for (let attempt = 0; attempt < noOfRooms; attempt++) {
			const roomIdx = (startCursor + attempt) % noOfRooms
			const room = rooms[roomIdx]
			const total = room.aweb + room.cwebAges.length + room.cnbAges.length
			if (total >= constraints.allowedExtraBeds) continue
			if (kind === "aweb" && room.aweb >= constraints.allowedAwebPerRoom)
				continue
			if (
				kind === "aweb" &&
				room.aweb >= constraints.maxAdultOccupancy - personsPerRoom
			)
				continue
			if (
				kind === "cweb" &&
				room.cwebAges.length >= constraints.allowedCwebPerRoom
			)
				continue
			if (
				kind === "cnb" &&
				constraints.allowedCnbPerRoom !== null &&
				room.cnbAges.length >= constraints.allowedCnbPerRoom
			)
				continue
			if (
				(kind === "cweb" || kind === "cnb") &&
				room.cwebAges.length + room.cnbAges.length >=
					constraints.maxChildOccupancy
			)
				continue

			if (kind === "aweb") room.aweb++
			else if (kind === "cweb") room.cwebAges.push(age as number)
			else room.cnbAges.push(age as number)
			return (roomIdx + 1) % noOfRooms
		}
		const roomIdx = startCursor % noOfRooms
		if (kind === "aweb") rooms[roomIdx].aweb++
		else if (kind === "cweb") rooms[roomIdx].cwebAges.push(age as number)
		else rooms[roomIdx].cnbAges.push(age as number)
		return (roomIdx + 1) % noOfRooms
	}

	let cursor = 0
	for (let i = 0; i < awebCount; i++) cursor = assign("aweb", undefined, cursor)
	cursor = 0
	for (const age of sortAgesDescending(cwebAges))
		cursor = assign("cweb", age, cursor)
	cursor = 0
	for (const age of sortAgesDescending(cnbAges))
		cursor = assign("cnb", age, cursor)

	return rooms
}

function computeTaxFromDistributions({
	baseRate,
	awebRate,
	getCwebRate,
	getCnbRate,
	slabDist,
	chargeDist,
}: {
	baseRate: TMoney
	awebRate: TMoney | undefined
	getCwebRate: (age: number) => TMoney | undefined
	getCnbRate: (age: number) => TMoney | undefined
	slabDist: Array<TRoomExtraDistribution>
	chargeDist: Array<TRoomExtraDistribution>
}): { totalAfterTax: TMoney; roomTaxRates: Array<number> } | undefined {
	let totalAfterTax: TMoney | undefined
	const roomTaxRates: Array<number> = []

	for (let i = 0; i < slabDist.length; i++) {
		const slab = slabDist[i]
		const charge = chargeDist[i]

		// Step 1 — this room's slab total, deciding its GST rate only.
		let slabTotal = baseRate
		if (slab.aweb && awebRate) {
			slabTotal = addMoney(slabTotal, multipleMoneyBy(awebRate, slab.aweb))
		}
		for (const age of slab.cwebAges) {
			const rate = getCwebRate(age)
			if (rate === undefined) return undefined
			slabTotal = addMoney(slabTotal, rate)
		}
		for (const age of slab.cnbAges) {
			const rate = getCnbRate(age)
			if (rate === undefined) return undefined
			slabTotal = addMoney(slabTotal, rate)
		}
		const taxRate = getGSTRate(slabTotal)
		roomTaxRates.push(taxRate)

		// Step 2 — apply that rate to what's actually charged in this room.
		let roomAfterTax = addPercentageToMoney(baseRate, taxRate)
		if (charge.aweb && awebRate) {
			roomAfterTax = addMoney(
				roomAfterTax,
				multipleMoneyBy(addPercentageToMoney(awebRate, taxRate), charge.aweb)
			)
		}
		for (const age of charge.cwebAges) {
			const rate = getCwebRate(age)
			if (rate === undefined) return undefined
			roomAfterTax = addMoney(roomAfterTax, addPercentageToMoney(rate, taxRate))
		}
		for (const age of charge.cnbAges) {
			const rate = getCnbRate(age)
			if (rate === undefined) return undefined
			roomAfterTax = addMoney(roomAfterTax, addPercentageToMoney(rate, taxRate))
		}

		totalAfterTax = totalAfterTax
			? addMoney(totalAfterTax, roomAfterTax)
			: roomAfterTax
	}

	if (!totalAfterTax) return undefined
	return { totalAfterTax, roomTaxRates }
}

type TRoomAllocationOccupancy = {
	noOfRooms: number
	personsPerRoom: number
	awebCount: number
	cwebAges: Array<number>
	cnbAges: Array<number>
}

type TRoomAllocationConstraints = {
	baseOccupancy: number
	allowedExtraBeds: number
	allowedAwebPerRoom: number
	allowedCwebPerRoom: number
	allowedCnbPerRoom: number | null
	maxAdultOccupancy: number
	maxChildOccupancy: number
}

function computeRoomAllocationAndTaxCore(
	occupancy: TRoomAllocationOccupancy,
	constraints: TRoomAllocationConstraints,
	rates: {
		baseRate: TMoney
		awebRate: TMoney | undefined
		getCwebRate: (age: number) => TMoney | undefined
		getCnbRate: (age: number) => TMoney | undefined
	}
): { winner: TTaxStrategyResult; taxSavings: TMoney } | undefined {
	const { noOfRooms, personsPerRoom, awebCount, cwebAges, cnbAges } = occupancy
	const { baseRate, awebRate, getCwebRate, getCnbRate } = rates

	const { slabDist: fbSlabDist, chargeDist: fbChargeDist } =
		distributeExtrasFillBase(
			noOfRooms,
			personsPerRoom,
			awebCount,
			cwebAges,
			cnbAges,
			constraints
		)
	const fbTax = computeTaxFromDistributions({
		baseRate,
		awebRate,
		getCwebRate,
		getCnbRate,
		slabDist: fbSlabDist,
		chargeDist: fbChargeDist,
	})
	if (!fbTax) return undefined

	const rrDist = distributeExtrasRoundRobin(
		noOfRooms,
		personsPerRoom,
		awebCount,
		cwebAges,
		cnbAges,
		constraints
	)
	const rrTax = computeTaxFromDistributions({
		baseRate,
		awebRate,
		getCwebRate,
		getCnbRate,
		slabDist: rrDist,
		chargeDist: rrDist,
	})
	if (!rrTax) return undefined

	const fbTotal = Number(formatMoneyByDecimal(fbTax.totalAfterTax))
	const rrTotal = Number(formatMoneyByDecimal(rrTax.totalAfterTax))

	const winner: TTaxStrategyResult =
		fbTotal <= rrTotal
			? {
					strategy: "fill_base",
					distribution: fbChargeDist,
					totalAfterTax: fbTax.totalAfterTax,
					roomTaxRates: fbTax.roomTaxRates,
				}
			: {
					strategy: "round_robin",
					distribution: rrDist,
					totalAfterTax: rrTax.totalAfterTax,
					roomTaxRates: rrTax.roomTaxRates,
				}

	const loserTotal =
		fbTotal <= rrTotal ? rrTax.totalAfterTax : fbTax.totalAfterTax
	const taxSavings = subtractMoney(loserTotal, winner.totalAfterTax)

	return { winner, taxSavings }
}

export function computeRoomAllocationAndTax(
	rateOption: IAvailabilityRateOption,
	date: string,
	occupancy: TRoomAllocationOccupancy,
	constraints: TRoomAllocationConstraints,
	discountFactor = 1
): { winner: TTaxStrategyResult; taxSavings: TMoney } | undefined {
	const baseResult = getRoomBasePrice(
		rateOption,
		occupancy.personsPerRoom,
		date,
		discountFactor
	)
	if (!baseResult) return undefined
	// discount is applied on the base rate only for now
	const awebRate = findRate(rateOption, date, "AWEB")

	return computeRoomAllocationAndTaxCore(occupancy, constraints, {
		baseRate: baseResult.amount,
		awebRate,
		getCwebRate: (age) => getChildRate(rateOption, date, "CWEB", age),
		getCnbRate: (age) => getChildRate(rateOption, date, "CNB", age),
	})
}

export function computeRoomAllocationAndTaxForGivenRates(
	occupancy: TRoomAllocationOccupancy,
	constraints: TRoomAllocationConstraints,
	givenRates: {
		baseRate: TMoney
		awebRate: TMoney | undefined
		cwebRatesByAge: Map<number, TMoney>
		cnbRatesByAge: Map<number, TMoney>
	}
): { winner: TTaxStrategyResult; taxSavings: TMoney } | undefined {
	return computeRoomAllocationAndTaxCore(occupancy, constraints, {
		baseRate: givenRates.baseRate,
		awebRate: givenRates.awebRate,
		getCwebRate: (age) => givenRates.cwebRatesByAge.get(age),
		getCnbRate: (age) => givenRates.cnbRatesByAge.get(age),
	})
}

// Complimentary stays completely outside the tax comparison — it's always
// ₹0, so it can never affect which GST slab a room falls into. Still has to
// respect max_child_occupancy jointly with the CWEB/CNB already assigned to
// each room (existingChildCountPerRoom), same "skip full rooms" shape as the
// other distributors.
export function distributeComplimentaryAcrossRooms(
	noOfRooms: number,
	complimentaryAges: Array<number>,
	existingChildCountPerRoom: Array<number>,
	maxChildOccupancy: number
): Array<Array<number>> {
	const rooms: Array<Array<number>> = Array.from(
		{ length: noOfRooms },
		() => []
	)
	let cursor = 0
	for (const age of sortAgesDescending(complimentaryAges)) {
		let assigned = false
		for (let attempt = 0; attempt < noOfRooms; attempt++) {
			const roomIdx = (cursor + attempt) % noOfRooms
			const existing = existingChildCountPerRoom[roomIdx] ?? 0
			if (existing + rooms[roomIdx].length >= maxChildOccupancy) continue
			rooms[roomIdx].push(age)
			cursor = (roomIdx + 1) % noOfRooms
			assigned = true
			break
		}
		if (!assigned) {
			const roomIdx = cursor % noOfRooms
			rooms[roomIdx].push(age)
			cursor = (cursor + 1) % noOfRooms
		}
	}
	return rooms
}

// IMPORTANT: `total` here is already the grand total across ALL `qty` rooms
// for the night — not a per-room figure. Distribution means different rooms
// can resolve to different breakdowns (esp. under OBP, where each room's own
// adult/child tier depends on how many extras actually landed in it), so
// there is no single "per room" price to multiply by qty anymore — the sum
// across rooms IS the total.
export function getNightlyPricesForRoomGroup(
	rateOption: IAvailabilityRateOption,
	roomType: IHotelRemoteRoomType,
	dateStrings: Array<string>,
	occupancy: {
		qty: number
		ppr: number
		aweb: number
		cwebAges: Array<number>
		cnbAges: Array<number>
		complimentaryAges: Array<number>
		discountPercent?: number
	}
): Array<TNightlyPrice> | undefined {
	const { qty, ppr, aweb, cwebAges, cnbAges, complimentaryAges } = occupancy
	const noOfRooms = Math.max(qty, 1)
	const otaRoomType = roomType.ota_room_type
	const discountFactor = (100 - (occupancy.discountPercent || 0)) / 100

	const nightly: Array<TNightlyPrice> = []

	for (const date of dateStrings) {
		const dateEntry = rateOption.dates.find((d) => d.date_local === date)
		if (!dateEntry) return undefined

		const rooms: Array<TRoomNightAllocation> = []
		let taxSavings: TMoney | undefined
		let taxStrategy: "fill_base" | "round_robin" | undefined

		if (dateEntry.pricing_type === "RBP") {
			const result = computeRoomAllocationAndTax(
				rateOption,
				date,
				{
					noOfRooms,
					personsPerRoom: ppr,
					awebCount: aweb,
					cwebAges,
					cnbAges,
				},
				{
					baseOccupancy: otaRoomType.base_adult_occupancy,
					allowedExtraBeds: roomType.allowed_extra_beds,
					allowedAwebPerRoom: roomType.allowed_adults_with_extra_bed,
					allowedCwebPerRoom: roomType.allowed_children_with_extra_bed,
					allowedCnbPerRoom:
						roomType.allowed_children_without_extra_bed ?? null,
					maxAdultOccupancy: otaRoomType.max_adult_occupancy,
					maxChildOccupancy: otaRoomType.max_child_occupancy,
				},
				discountFactor
			)

			if (!result) return undefined
			taxSavings = result.taxSavings
			taxStrategy = result.winner.strategy

			const complimentaryDist = distributeComplimentaryAcrossRooms(
				noOfRooms,
				complimentaryAges,
				result.winner.distribution.map(
					(d) => d.cwebAges.length + d.cnbAges.length
				),
				otaRoomType.max_child_occupancy
			)

			for (let i = 0; i < result.winner.distribution.length; i++) {
				const allocation = result.winner.distribution[i]
				const breakdown = getRoomPriceBreakdownForNight(
					rateOption,
					date,
					dateEntry.pricing_type,
					{
						ppr,
						aweb: allocation.aweb,
						cwebAges: allocation.cwebAges,
						cnbAges: allocation.cnbAges,
						complimentaryAges: complimentaryDist[i],
					},
					discountFactor
				)
				if (!breakdown || breakdown.pricing_type !== "RBP") return undefined

				// Reuses this room's already-decided allocation (not a fresh
				// allocation run) so it's always the same pax as `breakdown`,
				// just priced without the discount — safe to compare the two.
				const originalBreakdown = getRoomPriceBreakdownForNight(
					rateOption,
					date,
					dateEntry.pricing_type,
					{
						ppr,
						aweb: allocation.aweb,
						cwebAges: allocation.cwebAges,
						cnbAges: allocation.cnbAges,
						complimentaryAges: complimentaryDist[i],
					}
				)
				if (!originalBreakdown) return undefined

				const beforeTaxTotal = getTotalFromBreakdown(breakdown)
				const taxRate = result.winner.roomTaxRates[i]
				const afterTax = addPercentageToMoney(beforeTaxTotal, taxRate)
				breakdown.tax = {
					rate: taxRate,
					amount: subtractMoney(afterTax, beforeTaxTotal),
				}

				// Same room's allocation and tax rate, just priced without the
				// discount — the pre-discount after-tax total for this room.
				const originalBeforeTaxTotal = getTotalFromBreakdown(originalBreakdown)
				const originalAfterTax = addPercentageToMoney(
					originalBeforeTaxTotal,
					taxRate
				)

				rooms.push({
					room_index: i,
					breakdown,
					original_breakdown: originalBreakdown,
					total: getTotalFromBreakdown(breakdown),
					original_total: originalAfterTax,
				})
			}
		} else {
			// OBP — unchanged, tax stays out of scope here, same reasoning as
			// the PriceInput modal being RBP-only.
			const allocations = distributeExtrasAcrossRooms(
				noOfRooms,
				ppr,
				aweb,
				cwebAges,
				cnbAges,
				complimentaryAges,
				{
					allowedExtraBeds: roomType.allowed_extra_beds,
					allowedAwebPerRoom: roomType.allowed_adults_with_extra_bed,
					allowedCwebPerRoom: roomType.allowed_children_with_extra_bed,
					allowedCnbPerRoom:
						roomType.allowed_children_without_extra_bed ?? null,
					maxAdultOccupancy: otaRoomType.max_adult_occupancy,
					maxChildOccupancy: otaRoomType.max_child_occupancy,
				}
			)

			for (let i = 0; i < allocations.length; i++) {
				const allocation = allocations[i]
				const breakdown = getRoomPriceBreakdownForNight(
					rateOption,
					date,
					dateEntry.pricing_type,
					{
						ppr,
						aweb: allocation.aweb,
						cwebAges: allocation.cwebAges,
						cnbAges: allocation.cnbAges,
						complimentaryAges: allocation.complimentaryAges,
					},
					discountFactor
				)
				if (!breakdown) return undefined

				rooms.push({
					room_index: i,
					breakdown,
					original_breakdown: breakdown,
					total: getTotalFromBreakdown(breakdown),
					original_total: getTotalFromBreakdown(breakdown),
				})
			}
		}

		const total = addMoney(
			rooms[0].total,
			...rooms.slice(1).map((r) => r.total)
		)
		const originalTotal = addMoney(
			rooms[0].original_total,
			...rooms.slice(1).map((r) => r.original_total)
		)

		let rbpBreakdown:
			| Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>
			| undefined
		let originalRbpBreakdown:
			| Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }>
			| undefined
		if (dateEntry.pricing_type === "RBP") {
			const result = getRoomPriceBreakdownForNight(
				rateOption,
				date,
				dateEntry.pricing_type,
				{ ppr, aweb, cwebAges, cnbAges, complimentaryAges },
				discountFactor
			)
			if (!result || result.pricing_type !== "RBP") return undefined
			rbpBreakdown = result

			//originalResult - Purpose is to get breakdown with original prices without discount factor
			const originalResult = getRoomPriceBreakdownForNight(
				rateOption,
				date,
				dateEntry.pricing_type,
				{ ppr, aweb, cwebAges, cnbAges, complimentaryAges }
			)
			if (!originalResult || originalResult.pricing_type !== "RBP")
				return undefined
			originalRbpBreakdown = originalResult
		}

		nightly.push({
			date,
			rooms,
			rbp_breakdown: rbpBreakdown,
			original_rbp_breakdown: originalRbpBreakdown,
			total,
			original_total: originalTotal,
			tax_savings: taxSavings,
			tax_strategy: taxStrategy,
			rate_sheet: dateEntry.rate_sheet,
			rate_plan: dateEntry.rate_plan,
		})
	}

	return nightly
}

export function getTotalPriceForRoomGroup(
	rateOption: IAvailabilityRateOption,
	roomType: IHotelRemoteRoomType,
	dateStrings: Array<string>,
	occupancy: {
		qty: number
		ppr: number
		aweb: number
		cwebAges: Array<number>
		cnbAges: Array<number>
		complimentaryAges: Array<number>
		discountPercent?: number
	}
): TMoney | undefined {
	const nightly = getNightlyPricesForRoomGroup(
		rateOption,
		roomType,
		dateStrings,
		occupancy
	)
	if (!nightly?.length) return undefined
	return addMoney(nightly[0].total, ...nightly.slice(1).map((n) => n.total))
}

function findChildRateEntry(
	rateOption: IAvailabilityRateOption,
	date: string,
	kind: "CWEB" | "CNB",
	age: number
): { rate: TMoney; occupancy: string } | undefined {
	const dateEntry = rateOption.dates.find((d) => d.date_local === date)
	if (!dateEntry) return undefined

	// 1. Specific age-band entry matching this exact age (e.g. "CWEB (6-8)").
	// Matching is on the numeric start_age/end_age bounds, not the `occupancy`
	// label — the label is BE-defined and only meant for display here.
	const ageGroupEntry = dateEntry.rates.find(
		(r) =>
			r.pax_type === kind &&
			r.start_age != null &&
			r.end_age != null &&
			age >= r.start_age &&
			age <= r.end_age
	)
	if (ageGroupEntry) {
		return {
			rate: ageGroupEntry.rate_before_tax_money,
			occupancy: ageGroupEntry.occupancy,
		}
	}

	// 2. Flat, non-age-banded fallback ("CWEB" / "CNB").
	const flatPrice = findRate(rateOption, date, kind)
	if (flatPrice) return { rate: flatPrice, occupancy: kind }

	// 3. Not complimentary, and no price found anywhere — unavailable.
	return undefined
}

export function findMissingOccupancyRate(
	rateOption: IAvailabilityRateOption,
	dateStrings: Array<string>,
	occupancy: {
		aweb: number
		cwebAges: Array<number>
		cnbAges: Array<number>
	}
):
	| { date: string; pax_type: "AWEB" | "CWEB" | "CNB"; age?: number }
	| undefined {
	for (const date of dateStrings) {
		if (
			occupancy.aweb > 0 &&
			findRate(rateOption, date, "AWEB") === undefined
		) {
			return { date, pax_type: "AWEB" }
		}
		for (const age of occupancy.cwebAges) {
			if (!findChildRateEntry(rateOption, date, "CWEB", age)) {
				return { date, pax_type: "CWEB", age }
			}
		}
		for (const age of occupancy.cnbAges) {
			if (!findChildRateEntry(rateOption, date, "CNB", age)) {
				return { date, pax_type: "CNB", age }
			}
		}
	}
	return undefined
}

export function getChildRate(
	rateOption: IAvailabilityRateOption,
	date: string,
	kind: "CWEB" | "CNB",
	age: number
): TMoney | undefined {
	return findChildRateEntry(rateOption, date, kind, age)?.rate
}

// Avg per-night extra rate across the stay; undefined if any night has no rate
export function getExtraNightlyRate(
	rateOption: IAvailabilityRateOption,
	dateStrings: Array<string>,
	kind: "AWEB" | "CWEB" | "CNB",
	age?: number
): { occupancy: string; avg: TMoney; varies: boolean } | undefined {
	const entries: Array<{ rate: TMoney; occupancy: string }> = []
	for (const date of dateStrings) {
		if (kind === "AWEB") {
			const rate = findRate(rateOption, date, "AWEB")
			if (!rate) return undefined
			entries.push({ rate, occupancy: "AWEB" })
			continue
		}
		const entry =
			age === undefined
				? undefined
				: findChildRateEntry(rateOption, date, kind, age)
		if (!entry) return undefined
		entries.push(entry)
	}
	const [first, ...rest] = entries
	if (!first) return undefined
	return {
		occupancy: first.occupancy,
		avg: divideMoneyBy(
			addMoney(first.rate, ...rest.map((e) => e.rate)),
			entries.length
		),
		varies: rest.some((e) => !isZeroMoney(subtractMoney(e.rate, first.rate))),
	}
}

function getChildRates(
	rateOption: IAvailabilityRateOption,
	date: string,
	kind: "CWEB" | "CNB",
	ages: Array<number>
): Array<{ age: number; rate: TMoney; occupancy: string }> | undefined {
	const rates: Array<{ age: number; rate: TMoney; occupancy: string }> = []
	for (const age of ages) {
		const entry = findChildRateEntry(rateOption, date, kind, age)
		if (entry === undefined) return undefined
		rates.push({ age, rate: entry.rate, occupancy: entry.occupancy })
	}
	return rates
}

export type TRoomNightPriceBreakdown =
	| {
			pricing_type: "RBP"
			base: {
				tier: string
				amount: TMoney
			}
			aweb?: { count: number; rate: TMoney; amount: TMoney }
			cweb?: {
				children: Array<{ age: number; rate: TMoney; occupancy: string }>
				amount: TMoney
			}
			cnb?: {
				children: Array<{ age: number; rate: TMoney; occupancy: string }>
				amount: TMoney
			}
			complimentary?: { children: Array<{ age: number }>; amount: TMoney }
			tax?: { rate: number; amount: TMoney }
	  }
	| {
			pricing_type: "OBP"
			adult_tier: string
			adult_amount: TMoney
			child_tier?: string
			child_amount?: TMoney
	  }

export type TRoomAllocation = {
	aweb: number
	cwebAges: Array<number>
	cnbAges: Array<number>
	complimentaryAges: Array<number>
}

function distributeExtrasAcrossRooms(
	noOfRooms: number,
	personsPerRoom: number,
	awebCount: number,
	cwebAges: Array<number>,
	cnbAges: Array<number>,
	complimentaryAges: Array<number>,
	constraints: {
		allowedExtraBeds: number
		allowedAwebPerRoom: number
		allowedCwebPerRoom: number
		allowedCnbPerRoom: number | null
		maxAdultOccupancy: number
		maxChildOccupancy: number
	}
): Array<TRoomAllocation> {
	const rooms: Array<TRoomAllocation> = Array.from(
		{ length: noOfRooms },
		() => ({
			aweb: 0,
			cwebAges: [],
			cnbAges: [],
			complimentaryAges: [],
		})
	)

	type QueueItem =
		| { kind: "aweb" }
		| { kind: "cweb"; age: number }
		| { kind: "cnb"; age: number }
		| { kind: "complimentary"; age: number }

	const queue: Array<QueueItem> = [
		...Array.from({ length: awebCount }, (): QueueItem => ({ kind: "aweb" })),
		...sortAgesDescending(cwebAges).map(
			(age): QueueItem => ({ kind: "cweb", age })
		),
		...sortAgesDescending(cnbAges).map(
			(age): QueueItem => ({ kind: "cnb", age })
		),
		...sortAgesDescending(complimentaryAges).map(
			(age): QueueItem => ({ kind: "complimentary", age })
		),
	]

	let cursor = 0
	for (const item of queue) {
		let assigned = false
		for (let attempt = 0; attempt < noOfRooms; attempt++) {
			const roomIdx = (cursor + attempt) % noOfRooms
			const room = rooms[roomIdx]
			const total = room.aweb + room.cwebAges.length + room.cnbAges.length
			const childCount =
				room.cwebAges.length +
				room.cnbAges.length +
				room.complimentaryAges.length

			if (
				item.kind !== "complimentary" &&
				total >= constraints.allowedExtraBeds
			)
				continue
			if (item.kind === "aweb" && room.aweb >= constraints.allowedAwebPerRoom)
				continue
			if (
				item.kind === "aweb" &&
				room.aweb >= constraints.maxAdultOccupancy - personsPerRoom
			)
				continue
			if (
				item.kind === "cweb" &&
				room.cwebAges.length >= constraints.allowedCwebPerRoom
			)
				continue
			if (
				item.kind === "cnb" &&
				constraints.allowedCnbPerRoom !== null &&
				room.cnbAges.length >= constraints.allowedCnbPerRoom
			)
				continue
			if (item.kind !== "aweb" && childCount >= constraints.maxChildOccupancy)
				continue

			if (item.kind === "aweb") room.aweb++
			else if (item.kind === "cweb") room.cwebAges.push(item.age)
			else if (item.kind === "cnb") room.cnbAges.push(item.age)
			else room.complimentaryAges.push(item.age)

			cursor = (roomIdx + 1) % noOfRooms
			assigned = true
			break
		}

		if (!assigned) {
			const room = rooms[cursor % noOfRooms]
			if (item.kind === "aweb") room.aweb++
			else if (item.kind === "cweb") room.cwebAges.push(item.age)
			else if (item.kind === "cnb") room.cnbAges.push(item.age)
			else room.complimentaryAges.push(item.age)
			cursor = (cursor + 1) % noOfRooms
		}
	}

	return rooms
}

export function getRoomPriceBreakdownForNight(
	rateOption: IAvailabilityRateOption,
	date: string,
	pricingType: THotelRatePricingType,
	occupancy: {
		ppr: number
		aweb: number
		cwebAges: Array<number>
		cnbAges: Array<number>
		complimentaryAges: Array<number>
	},
	discountFactor = 1
): TRoomNightPriceBreakdown | undefined {
	const { ppr, aweb, cwebAges, cnbAges, complimentaryAges } = occupancy

	if (pricingType === "OBP") {
		const totalAdults = ppr + aweb
		const adultTier = `A${totalAdults}`
		const adultAmount = findRate(rateOption, date, adultTier, discountFactor)
		if (adultAmount === undefined) return undefined

		const chargeableChildren = cwebAges.length + cnbAges.length

		if (chargeableChildren <= 0) {
			return {
				pricing_type: "OBP",
				adult_tier: adultTier,
				adult_amount: adultAmount,
			}
		}

		const childTier = `C${chargeableChildren}`
		const childAmount = findRate(rateOption, date, childTier, discountFactor)
		if (childAmount === undefined) return undefined

		return {
			pricing_type: "OBP",
			adult_tier: adultTier,
			adult_amount: adultAmount,
			child_tier: childTier,
			child_amount: childAmount,
		}
	}

	// RBP
	const baseResult = getRoomBasePrice(rateOption, ppr, date, discountFactor)
	if (baseResult === undefined) return undefined

	const breakdown: Extract<TRoomNightPriceBreakdown, { pricing_type: "RBP" }> =
		{
			pricing_type: "RBP",
			base: {
				tier: baseResult.tier,
				amount: baseResult.amount,
			},
		}

	// extras at the sheet rate, discount is on the base rate only for now
	if (aweb > 0) {
		const rate = findRate(rateOption, date, "AWEB")
		if (rate === undefined) return undefined
		breakdown.aweb = {
			count: aweb,
			rate,
			amount: multipleMoneyBy(rate, aweb),
		}
	}
	if (cwebAges.length > 0) {
		const children = getChildRates(rateOption, date, "CWEB", cwebAges)
		if (!children) return undefined
		breakdown.cweb = {
			children,
			amount: addMoney(
				children[0].rate,
				...children.slice(1).map((c) => c.rate)
			),
		}
	}
	if (cnbAges.length > 0) {
		const children = getChildRates(rateOption, date, "CNB", cnbAges)
		if (!children) return undefined
		breakdown.cnb = {
			children,
			amount: addMoney(
				children[0].rate,
				...children.slice(1).map((c) => c.rate)
			),
		}
	}

	if (complimentaryAges.length > 0) {
		breakdown.complimentary = {
			children: complimentaryAges.map((age) => ({ age })),
			amount: createMoney(0, breakdown.base.amount.currency),
		}
	}

	return breakdown
}

export function getTotalFromBreakdown(
	breakdown: TRoomNightPriceBreakdown
): TMoney {
	if (breakdown.pricing_type === "OBP") {
		return breakdown.child_amount
			? addMoney(breakdown.adult_amount, breakdown.child_amount)
			: breakdown.adult_amount
	}

	const amounts: Array<TMoney> = [breakdown.base.amount]
	if (breakdown.aweb) amounts.push(breakdown.aweb.amount)
	if (breakdown.cweb) amounts.push(breakdown.cweb.amount)
	if (breakdown.cnb) amounts.push(breakdown.cnb.amount)
	if (breakdown.complimentary) amounts.push(breakdown.complimentary.amount)
	if (breakdown.tax) amounts.push(breakdown.tax.amount)
	return addMoney(amounts[0], ...amounts.slice(1))
}

// Match by the stable ota_room_type/ota_meal_plan ids, not the local wrapper
// ids — BE re-clones the local room type/meal plan on a name/config change,
// so the wrapper id can't be trusted to survive a version bump. Shared by the
// "Check Availability" modal's re-edit drift check and the background
// stale-rate/availability check below, so both can never disagree.
export function findMatchingRateOption(
	availability: IAvailability,
	otaRoomTypeId: number,
	otaMealPlanId: number
):
	| {
			roomTypeWiseRate: IAvailabilityRoomWiseRateOptions
			rateOption: IAvailabilityRateOption
	  }
	| undefined {
	const roomTypeWiseRate = availability.room_type_wise_rates.find(
		(item) => item.room_type.ota_room_type.id === otaRoomTypeId
	)
	if (!roomTypeWiseRate) return undefined

	const rateOption = roomTypeWiseRate.rate_options.find(
		(option) => option.meal_plan.ota_meal_plan.id === otaMealPlanId
	)
	if (!rateOption) return undefined

	return { roomTypeWiseRate, rateOption }
}

function toDecimal(money: TMoney): number {
	return Number(formatMoneyByDecimal(money))
}

export type TStaleAvailabilityWarning =
	| { type: "unavailable"; message: string }
	| { type: "rooms_reduced"; availableRooms: number; message: string }
	| { type: "details_changed"; message: string }
	| { type: "rate_changed"; message: string }

type TNamedEntity = { id: number; name: string }

// Renames create a new local version (new id) of the same OTA room type/meal plan,
// e.g. 'Room type renamed from "Deluxe" to "Deluxe Sea View" since this quote was made.'
export function describeSelectionDetailsChange({
	savedRoomType,
	currentRoomType,
	savedMealPlan,
	currentMealPlan,
}: {
	savedRoomType: TNamedEntity
	currentRoomType: TNamedEntity
	savedMealPlan: TNamedEntity
	currentMealPlan: TNamedEntity
}): string | undefined {
	const roomTypeRenamed = savedRoomType.id !== currentRoomType.id
	const mealPlanRenamed = savedMealPlan.id !== currentMealPlan.id
	const roomTypeChange = `"${savedRoomType.name}" → "${currentRoomType.name}"`
	const mealPlanChange = `"${savedMealPlan.name}" → "${currentMealPlan.name}"`
	if (roomTypeRenamed && mealPlanRenamed)
		return `Room type and meal plan renamed since this quote was made (${roomTypeChange}, ${mealPlanChange}).`
	if (roomTypeRenamed)
		return `Room type renamed from "${savedRoomType.name}" to "${currentRoomType.name}" since this quote was made.`
	if (mealPlanRenamed)
		return `Meal plan renamed from "${savedMealPlan.name}" to "${currentMealPlan.name}" since this quote was made.`
	return undefined
}

/**
 * Compares a quote hotel's stored per-night price breakdowns against a
 * freshly-fetched availability response to detect if the selection is no
 * longer bookable, room count now exceeds what's available, or rates moved
 * since the quote was saved. Each night is compared against its own stored
 * breakdown — rates (and even the rate source) can legitimately differ
 * night-to-night, so one night's breakdown is never checked against another.
 */
export function detectStaleAvailability({
	availability,
	otaRoomTypeId,
	otaMealPlanId,
	savedRoomType,
	savedMealPlan,
	noOfRooms,
	storedDateWisePrices,
}: {
	availability: IAvailability
	otaRoomTypeId: number
	otaMealPlanId: number
	savedRoomType: TNamedEntity
	savedMealPlan: TNamedEntity
	noOfRooms: number
	storedDateWisePrices: Array<{
		date: string
		remote_price_breakdowns?: THotelPriceBreakdowns
	}>
}): TStaleAvailabilityWarning | null {
	const matched = findMatchingRateOption(
		availability,
		otaRoomTypeId,
		otaMealPlanId
	)
	if (!matched)
		return {
			type: "unavailable",
			message:
				"Availability has changed. This room type or rate plan is no longer available. Click Change to select another option.",
		}

	const { rateOption } = matched

	const dateStrings = storedDateWisePrices.map((p) => p.date)
	const matchedDates = rateOption.dates.filter((d) =>
		dateStrings.includes(d.date_local)
	)
	const availableRooms =
		matchedDates.length > 0
			? Math.min(...matchedDates.map((d) => d.available_rooms))
			: 0
	if (availableRooms < noOfRooms)
		return {
			type: "rooms_reduced",
			availableRooms,
			message: `Availability has changed. Only ${availableRooms} ${pluralize(`rooms`, availableRooms)} available now (${noOfRooms} currently selected). Click Change to update.`,
		}

	const detailsChange = describeSelectionDetailsChange({
		savedRoomType,
		currentRoomType: matched.roomTypeWiseRate.room_type,
		savedMealPlan,
		currentMealPlan: rateOption.meal_plan,
	})
	if (detailsChange)
		return {
			type: "details_changed",
			message: `${detailsChange} Click Change and confirm again to update it.`,
		}

	// Compares raw, before-tax/before-allocation, before-discount rates only
	// — original_rate_bt is the true API rate at save time, so this compares
	// apples-to-apples against a freshly fetched rate at no discount,
	// independent of whatever discount/given-price was applied on top.
	const hasRateChanged = storedDateWisePrices.some(
		({ date, remote_price_breakdowns }) =>
			(remote_price_breakdowns || []).some((row) => {
				if (row.pax_type === "COMP") return false
				const freshRate =
					row.pax_type === "CWEB" || row.pax_type === "CNB"
						? row.age !== undefined
							? getChildRate(rateOption, date, row.pax_type, row.age)
							: undefined
						: findRate(rateOption, date, row.occupancy)
				if (!freshRate) return true

				return toDecimal(freshRate) !== row.original_rate_bt
			})
	)
	if (hasRateChanged)
		return {
			type: "rate_changed",
			message:
				"Price has changed since this quote was last saved. Click Change to confirm new rates.",
		}

	return null
}

export type TPaxOverageRow = {
	dates?: Array<Date>
	hotel?: { name: string }
	room_type?: { name: string }
	meal_plan?: { name: string }
	no_of_rooms: number
	persons_per_room: number
	adults_with_extra_bed: number
	children_with_extra_bed_ages?: Array<number>
	children_without_extra_bed_ages?: Array<number>
	children_complimentary_ages?: Array<number>
}

export type TPaxOverageAllocation = {
	source: "existing" | "adding"
	// set for the modal's room types
	roomTypeId?: number
	hotelName: string
	roomTypeName: string
	mealPlanName: string
	rooms: number
	ppr: number
	aweb: number
	cwebAges: Array<number>
	cnbAges: Array<number>
	complimentaryAges: Array<number>
	heads: number
}

export type TNightlyPaxOverage = {
	date: string
	accommodated: number
	tripTotal: number
	overAges: Array<{ age: number; placed: number; inTrip: number }>
	allocations: Array<TPaxOverageAllocation>
}

export type TPaxOverageGroup = Omit<TNightlyPaxOverage, "date"> & {
	dates: Array<string>
}

function getAllocationHeads(
	a: Omit<TPaxOverageAllocation, "source" | "heads">
): number {
	return (
		a.ppr * a.rooms +
		a.aweb +
		a.cwebAges.length +
		a.cnbAges.length +
		a.complimentaryAges.length
	)
}

export type TNightPaxStatus = {
	date: string
	trip: { adults: number; childAges: Array<number>; total: number }
	placed: { adults: number; children: number; total: number }
	remaining: { adults: number; childAges: Array<number> }
	// children assumed in adult beds (beds beyond trip adults), oldest first
	bedChildAges: Array<number>
	extra: number
	overAges: Array<{ age: number; placed: number; inTrip: number }>
	allocations: Array<TPaxOverageAllocation>
}

type TNightlyPaxArgs = {
	tripNoOfAdults?: number | null
	tripChildren?: TChildrenArray | null
	dateStrings: Array<string>
	existingRows: Array<TPaxOverageRow>
	hotelName: string
	selections: Array<
		Pick<IConfirmedRoomSelection, "qty" | "occupancy"> & {
			room_type: { id?: number; name: string }
			meal_plan: { name: string }
		}
	>
}

function countByAge(ages: Array<number>): Map<number, number> {
	const counts = new Map<number, number>()
	for (const age of ages) counts.set(age, (counts.get(age) || 0) + 1)
	return counts
}

/**
 * Night-wise pax placement across existing rows + the selections in the modal.
 * Beds go to adults first; beds beyond trip adults hold the oldest unplaced children.
 */
export function getNightlyPaxStatus({
	tripNoOfAdults,
	tripChildren,
	dateStrings,
	existingRows,
	hotelName,
	selections,
}: TNightlyPaxArgs): Array<TNightPaxStatus> {
	const children = tripChildren || []
	if (!tripNoOfAdults && !children.length) return []

	const tripAdults = tripNoOfAdults || 0
	// trip children may repeat an age as separate entries
	const tripChildAges = children
		.flatMap((c) => Array.from({ length: c.count }, () => c.age))
		.sort((a, b) => b - a)
	const tripAgeCounts = countByAge(tripChildAges)
	const tripTotal = tripAdults + tripChildAges.length

	const confirmedRows = existingRows.filter((r) => r.room_type)
	const addingAllocations = selections.map((s): TPaxOverageAllocation => {
		const allocation = {
			hotelName,
			roomTypeId: s.room_type.id,
			roomTypeName: s.room_type.name,
			mealPlanName: s.meal_plan.name,
			rooms: s.qty,
			ppr: s.occupancy.ppr,
			aweb: s.occupancy.aweb,
			cwebAges: s.occupancy.cwebAges,
			cnbAges: s.occupancy.cnbAges,
			complimentaryAges: s.occupancy.complimentaryAges,
		}
		return {
			...allocation,
			source: "adding",
			heads: getAllocationHeads(allocation),
		}
	})

	return dateStrings.map((date): TNightPaxStatus => {
		const existingAllocations = confirmedRows
			.filter((r) =>
				(r.dates || []).some((d) => formatDate(d, "YYYY-MM-DD") === date)
			)
			.map((r): TPaxOverageAllocation => {
				const allocation = {
					hotelName: r.hotel?.name || "Hotel",
					roomTypeName: r.room_type?.name || "",
					mealPlanName: r.meal_plan?.name || "",
					rooms: Number(r.no_of_rooms || 0),
					ppr: Number(r.persons_per_room || 0),
					aweb: Number(r.adults_with_extra_bed || 0),
					cwebAges: r.children_with_extra_bed_ages || [],
					cnbAges: r.children_without_extra_bed_ages || [],
					complimentaryAges: r.children_complimentary_ages || [],
				}
				return {
					...allocation,
					source: "existing",
					heads: getAllocationHeads(allocation),
				}
			})
		const allocations = existingAllocations.concat(addingAllocations)
		const placedAges = allocations.flatMap((a) => [
			...a.cwebAges,
			...a.cnbAges,
			...a.complimentaryAges,
		])
		const beds = allocations.reduce(
			(sum, a) => sum + a.ppr * a.rooms + a.aweb,
			0
		)
		const placedAdults = Math.min(beds, tripAdults)
		const placedTotal = beds + placedAges.length

		const placedAgeCounts = countByAge(placedAges)
		const overAges = Array.from(placedAgeCounts.entries())
			.filter(([age, placed]) => placed > (tripAgeCounts.get(age) || 0))
			.map(([age, placed]) => ({
				age,
				placed,
				inTrip: tripAgeCounts.get(age) || 0,
			}))
			.sort((a, b) => a.age - b.age)

		// trip children not explicitly placed, oldest first
		const toSkip = new Map(placedAgeCounts)
		const unplacedAges = tripChildAges.filter((age) => {
			const skip = toSkip.get(age) || 0
			if (!skip) return true
			toSkip.set(age, skip - 1)
			return false
		})
		const bedChildren = beds - placedAdults

		return {
			date,
			trip: { adults: tripAdults, childAges: tripChildAges, total: tripTotal },
			placed: {
				adults: placedAdults,
				children: placedAges.length + bedChildren,
				total: placedTotal,
			},
			remaining: {
				adults: tripAdults - placedAdults,
				childAges: unplacedAges.slice(bedChildren),
			},
			bedChildAges: unplacedAges.slice(0, bedChildren),
			extra: Math.max(placedTotal - tripTotal, 0),
			overAges,
			allocations,
		}
	})
}

/**
 * Nights where pax placed exceed trip pax, or a child age is placed more often
 * than the trip has.
 */
export function getNightlyPaxOverages(
	args: TNightlyPaxArgs
): Array<TNightlyPaxOverage> {
	return getNightlyPaxStatus(args)
		.filter((s) => s.extra > 0 || s.overAges.length)
		.map(toNightlyPaxOverage)
}

/**
 * Over nights the modal's selections are responsible for: they add extra pax,
 * or place a child that ends up repeated. Problems caused only by other rows
 * don't block the modal.
 */
export function getBlockingPaxOverages(
	args: TNightlyPaxArgs
): Array<TNightlyPaxOverage> {
	const withSelections = getNightlyPaxStatus(args)
	const withoutSelections = getNightlyPaxStatus({ ...args, selections: [] })
	const selectionAges = new Set(
		args.selections.flatMap((s) => [
			...s.occupancy.cwebAges,
			...s.occupancy.cnbAges,
			...s.occupancy.complimentaryAges,
		])
	)
	return withSelections
		.filter((s, i) => {
			const addsExtra = s.extra > (withoutSelections[i]?.extra || 0)
			const repeatsChild = s.overAges.some((a) => selectionAges.has(a.age))
			return addsExtra || repeatsChild
		})
		.map(toNightlyPaxOverage)
}

function toNightlyPaxOverage(s: TNightPaxStatus): TNightlyPaxOverage {
	return {
		date: s.date,
		accommodated: s.placed.total,
		tripTotal: s.trip.total,
		overAges: s.overAges,
		allocations: s.allocations,
	}
}

/**
 * The tightest night: most pax placed, then fewest remaining.
 */
export function getWorstNightPaxStatus(
	statuses: Array<TNightPaxStatus>
): TNightPaxStatus | undefined {
	function remainingCount(s: TNightPaxStatus) {
		return s.remaining.adults + s.remaining.childAges.length
	}
	return statuses.reduce<TNightPaxStatus | undefined>((worst, s) => {
		if (!worst) return s
		if (s.placed.total !== worst.placed.total) {
			return s.placed.total > worst.placed.total ? s : worst
		}
		return remainingCount(s) < remainingCount(worst) ? s : worst
	}, undefined)
}

/**
 * Child ages still unplaced on every night, oldest first.
 */
export function getFreeChildAges(
	statuses: Array<TNightPaxStatus>
): Array<number> {
	return getAgesOnEveryNight(statuses.map((s) => s.remaining.childAges))
}

/**
 * Child ages not explicitly placed (free or assumed in an adult bed) on every
 * night, oldest first.
 */
export function getUnassignedChildAges(
	statuses: Array<TNightPaxStatus>
): Array<number> {
	return getAgesOnEveryNight(
		statuses.map((s) => s.remaining.childAges.concat(s.bedChildAges))
	)
}

export type TSpareBeds = { childAges: Array<number>; empty: number }

/**
 * Spare adult beds per allocation (same order as `status.allocations`).
 * Adults fill base beds in order (other rows, then the modal's room types),
 * then AWEB beds; what's left is spare. Assigned children fill AWEB spares
 * first, then base spares, in the same order; the rest are empty.
 */
export function getSpareBedsByAllocation(
	status: TNightPaxStatus
): Array<{ aweb: TSpareBeds; base: TSpareBeds }> {
	let adultsLeft = status.trip.adults
	const spare = status.allocations.map((a) => {
		const beds = a.ppr * a.rooms
		const taken = Math.min(beds, adultsLeft)
		adultsLeft -= taken
		return { base: beds - taken, aweb: 0 }
	})
	status.allocations.forEach((a, i) => {
		const taken = Math.min(a.aweb, adultsLeft)
		adultsLeft -= taken
		spare[i].aweb = a.aweb - taken
	})
	const children = [...status.bedChildAges]
	const result = spare.map(() => ({
		aweb: { childAges: [] as Array<number>, empty: 0 },
		base: { childAges: [] as Array<number>, empty: 0 },
	}))
	for (const kind of ["aweb", "base"] as const) {
		spare.forEach((s, i) => {
			const assigned = children.splice(0, s[kind])
			result[i][kind] = {
				childAges: assigned,
				empty: s[kind] - assigned.length,
			}
		})
	}
	return result
}

function getAgesOnEveryNight(nights: Array<Array<number>>): Array<number> {
	const first = nights[0]
	if (!first) return []
	const nightCounts = nights.map(countByAge)
	return Array.from(countByAge(first).keys())
		.sort((a, b) => b - a)
		.flatMap((age) => {
			const count = Math.min(...nightCounts.map((c) => c.get(age) || 0))
			return Array.from({ length: count }, () => age)
		})
}

export type TChildSlotBucket = "CWEB" | "CNB" | "COMP"

export type TChildSwapCandidate = {
	slot: IChildSlotOption
	// set when the candidate sits in another bucket of the same room type
	fromBucket?: TChildSlotBucket
}

function getChildRatesTotal(
	rateOption: IAvailabilityRateOption,
	dateStrings: Array<string>,
	children: Array<{ bucket: TChildSlotBucket; age: number }>
): TMoney | undefined | null {
	const rates: Array<TMoney> = []
	for (const { bucket, age } of children) {
		// complimentary children are free
		if (bucket === "COMP") continue
		for (const date of dateStrings) {
			const rate = getChildRate(rateOption, date, bucket, age)
			if (!rate) return undefined
			rates.push(rate)
		}
	}
	const [first, ...rest] = rates
	return first ? addMoney(first, ...rest) : null
}

function changesChildPrice(
	rateOption: IAvailabilityRateOption,
	dateStrings: Array<string>,
	before: Array<{ bucket: TChildSlotBucket; age: number }>,
	after: Array<{ bucket: TChildSlotBucket; age: number }>
): boolean {
	const beforeTotal = getChildRatesTotal(rateOption, dateStrings, before)
	const afterTotal = getChildRatesTotal(rateOption, dateStrings, after)
	// unknown rate: let the user decide
	if (beforeTotal === undefined || afterTotal === undefined) return true
	if (!beforeTotal || !afterTotal) return beforeTotal !== afterTotal
	return !isZeroMoney(subtractMoney(afterTotal, beforeTotal))
}

/**
 * Children a placed child can trade places with without changing the head
 * count: an eligible child in another bucket of the same room type, or one not
 * explicitly placed. Only swaps that change the price are returned.
 */
export function getChildSwapCandidates({
	slot,
	bucket,
	roomTypeSlots,
	bucketOptions,
	unassignedChildAges,
	rateOption,
	dateStrings,
}: {
	slot: IChildSlotOption
	bucket: TChildSlotBucket
	roomTypeSlots: Record<TChildSlotBucket, Array<IChildSlotOption>>
	bucketOptions: Record<TChildSlotBucket, Array<IChildSlotOption>>
	unassignedChildAges: Array<number>
	rateOption: IAvailabilityRateOption
	dateStrings: Array<string>
}): Array<TChildSwapCandidate> {
	function isEligible(child: IChildSlotOption, target: TChildSlotBucket) {
		return bucketOptions[target].some((o) => o.id === child.id)
	}

	const otherBuckets = (["CWEB", "CNB", "COMP"] as const).filter(
		(b) => b !== bucket
	)
	const inRoomType = otherBuckets.flatMap(
		(fromBucket): Array<TChildSwapCandidate> =>
			roomTypeSlots[fromBucket]
				.filter(
					(child) => isEligible(child, bucket) && isEligible(slot, fromBucket)
				)
				.filter((child) =>
					changesChildPrice(
						rateOption,
						dateStrings,
						[
							{ bucket, age: slot.age },
							{ bucket: fromBucket, age: child.age },
						],
						[
							{ bucket, age: child.age },
							{ bucket: fromBucket, age: slot.age },
						]
					)
				)
				.map((child) => ({ slot: child, fromBucket }))
	)

	const roomTypeIds = new Set(
		Object.values(roomTypeSlots).flatMap((slots) => slots.map((s) => s.id))
	)
	const seenAges = new Set<number>()
	const unassigned = bucketOptions[bucket]
		.filter((child) => {
			if (roomTypeIds.has(child.id) || seenAges.has(child.age)) return false
			if (!unassignedChildAges.includes(child.age)) return false
			seenAges.add(child.age)
			return changesChildPrice(
				rateOption,
				dateStrings,
				[{ bucket, age: slot.age }],
				[{ bucket, age: child.age }]
			)
		})
		.map((child) => ({ slot: child }))

	return inRoomType.concat(unassigned)
}

/**
 * Pick the child for the next stepper increment: an unplaced eligible child
 * first, then one only assumed in a spare adult bed, else any eligible child
 * not already in this room type.
 */
export function pickChildSlot({
	options,
	excludeIds,
	freeChildAges,
	unassignedChildAges,
	prefer,
}: {
	options: Array<IChildSlotOption>
	excludeIds: Array<number>
	freeChildAges: Array<number>
	unassignedChildAges: Array<number>
	prefer: "oldest" | "youngest"
}): IChildSlotOption | undefined {
	const candidates = options
		.filter((o) => !excludeIds.includes(o.id))
		.sort((a, b) => (prefer === "oldest" ? b.age - a.age : a.age - b.age))
	const freeCounts = countByAge(freeChildAges)
	const unassignedCounts = countByAge(unassignedChildAges)
	return (
		candidates.find((o) => (freeCounts.get(o.age) || 0) > 0) ||
		candidates.find((o) => (unassignedCounts.get(o.age) || 0) > 0) ||
		candidates[0]
	)
}

/**
 * Eligible children free on every modal night, in pick order. Used to prefill a
 * bucket without falling back to already placed children.
 */
export function pickFreeChildSlots({
	options,
	excludeIds,
	freeChildAges,
	prefer,
}: {
	options: Array<IChildSlotOption>
	excludeIds: Array<number>
	freeChildAges: Array<number>
	prefer: "oldest" | "youngest"
}): Array<IChildSlotOption> {
	const freeCounts = countByAge(freeChildAges)
	return options
		.filter((o) => !excludeIds.includes(o.id))
		.sort((a, b) => (prefer === "oldest" ? b.age - a.age : a.age - b.age))
		.filter((o) => {
			const count = freeCounts.get(o.age) || 0
			if (!count) return false
			freeCounts.set(o.age, count - 1)
			return true
		})
}

/**
 * Merge consecutive nights with identical allocations into one group.
 */
export function groupPaxOveragesByConsecutiveNights(
	overages: Array<TNightlyPaxOverage>
): Array<TPaxOverageGroup> {
	return groupConsecutiveNights(overages)
}

/**
 * Merge consecutive nights whose data (everything except the date) is identical.
 */
export function groupConsecutiveNights<T extends { date: string }>(
	nights: Array<T>
): Array<Omit<T, "date"> & { dates: Array<string> }> {
	const groups: Array<Omit<T, "date"> & { dates: Array<string> }> = []
	const keys: Array<string> = []
	for (const { date, ...rest } of nights) {
		const key = JSON.stringify(rest)
		const last = groups[groups.length - 1]
		const lastDate = last?.dates[last.dates.length - 1]
		if (
			last &&
			lastDate &&
			keys[keys.length - 1] === key &&
			formatDate(addUnit(lastDate, 1, "day"), "YYYY-MM-DD") === date
		) {
			last.dates.push(date)
		} else {
			groups.push({ ...rest, dates: [date] })
			keys.push(key)
		}
	}
	return groups
}

type TPriceBreakupLine = {
	occupancy: string
	quantity: number
	age?: number
	rate: TMoney
	// set only when the original rate is higher than the given rate
	originalRate?: TMoney
	amount: TMoney
}

export type TNightPriceBreakup = {
	date: string
	base?: TPriceBreakupLine
	aweb?: TPriceBreakupLine
	cweb: Array<TPriceBreakupLine>
	cnb: Array<TPriceBreakupLine>
	compAges: Array<number>
	tax: TMoney
	total: TMoney
}

// Night breakup from the saved RBP rows at given (or calculated) rates; tax is
// what's left of the night's total
export function getNightPriceBreakup(
	price: {
		date: string
		currency: string
		given_price?: number
		calculated_price?: number
		remote_pricing_type?: THotelRatePricingType
		remote_price_breakdowns?: THotelPriceBreakdowns
	},
	rateType: "given" | "calculated" = "given"
): TNightPriceBreakup | undefined {
	const rows = price.remote_price_breakdowns || []
	if (price.remote_pricing_type !== "RBP" || !rows.length) return undefined
	const { currency } = price

	const breakup: TNightPriceBreakup = {
		date: price.date,
		cweb: [],
		cnb: [],
		compAges: [],
		tax: createMoney(0, currency),
		total: moneyParseByDecimal(
			(rateType === "given" ? price.given_price : price.calculated_price) || 0,
			currency
		),
	}
	const amounts: Array<TMoney> = []
	for (const row of rows) {
		if (row.pax_type === "COMP") {
			if (row.age !== undefined) breakup.compAges.push(row.age)
			continue
		}
		const rate = moneyParseByDecimal(
			rateType === "given" ? row.given_rate_bt : row.calculated_rate_bt,
			currency
		)
		const original = moneyParseByDecimal(row.original_rate_bt, currency)
		const amount = multipleMoneyBy(rate, row.quantity)
		amounts.push(amount)
		const line: TPriceBreakupLine = {
			occupancy: row.occupancy,
			quantity: row.quantity,
			age: row.age,
			rate,
			originalRate:
				subtractMoney(original, rate).amount > 0 ? original : undefined,
			amount,
		}
		switch (row.pax_type) {
			case "BASE":
			case "A":
				breakup.base = line
				break
			case "AWEB":
				breakup.aweb = line
				break
			case "CWEB":
				breakup.cweb.push(line)
				break
			case "CNB":
				breakup.cnb.push(line)
				break
		}
	}
	const [first, ...rest] = amounts
	if (first)
		breakup.tax = subtractMoney(breakup.total, addMoney(first, ...rest))
	return breakup
}
